import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
    Plane, Clock, ArrowRight, Loader2, Fuel, Package, Trash2,
    CheckCircle, CircleDot, Droplets, Coffee, Snowflake, Luggage
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getLocalToday } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
    scheduled: 'Programado', confirmed: 'Confirmado', preparing: 'Preparando',
    boarding: 'Abordando', closed: 'Cerrado', departed: 'Despegado',
    en_route: 'En Vuelo', arrived: 'Llegado', delayed: 'Demorado',
};
const statusColors = {
    scheduled: 'bg-slate-500/10 text-slate-400',
    confirmed: 'bg-blue-500/10 text-blue-400',
    preparing: 'bg-cyan-500/10 text-cyan-400',
    boarding: 'bg-amber-500/10 text-amber-400',
    closed: 'bg-orange-500/10 text-orange-400',
    departed: 'bg-emerald-500/10 text-emerald-400',
    en_route: 'bg-green-100 text-green-800 border border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/40',
    arrived: 'bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-400/50',
    delayed: 'bg-yellow-100 text-yellow-800 border border-yellow-300 dark:bg-yellow-500/15 dark:text-yellow-300 dark:border-yellow-500/40',
    cancelled: 'bg-red-100 text-red-800 border border-red-300 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/40',
};

const checklistItems = [
    { key: 'chocks_placed', label: 'Calzos colocados', icon: CircleDot, responsible: 'Rampa' },
    { key: 'gpu_connected', label: 'GPU conectada', icon: Fuel, responsible: 'Rampa' },
    { key: 'fuel_completed', label: 'Combustible cargado', icon: Fuel, responsible: 'Despacho / Rampa' },
    { key: 'catering_loaded', label: 'Catering cargado', icon: Coffee, responsible: 'Rampa' },
    { key: 'water_serviced', label: 'Agua potable', icon: Droplets, responsible: 'Rampa' },
    { key: 'lavatory_serviced', label: 'Lavatorios', icon: Droplets, responsible: 'Rampa' },
    { key: 'cargo_loaded', label: 'Carga cargada', icon: Package, responsible: 'Rampa' },
    { key: 'baggage_loaded', label: 'Equipaje cargado', icon: Luggage, responsible: 'Rampa / Mostrador' },
    { key: 'cabin_cleaned', label: 'Cabina limpia', icon: CheckCircle, responsible: 'Rampa' },
    { key: 'deicing_applied', label: 'Deshielo aplicado', icon: Snowflake, responsible: 'Rampa' },
    { key: 'pushback_ready', label: 'Listo para pushback', icon: Plane, responsible: 'Rampa' },
];

export default function RampPage() {
    const { token, hasRole, airline } = useAuth();
    const navigate = useNavigate();
    const headers = { Authorization: `Bearer ${token}` };
    const [flights, setFlights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateFilter, setDateFilter] = useState(getLocalToday());
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [services, setServices] = useState({});
    const [saving, setSaving] = useState(false);

    const fetchFlights = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/ramp/flights?date=${dateFilter}`, { headers });
            setFlights(res.data);
        } catch { toast.error('Error al cargar vuelos'); }
        finally { setLoading(false); }
    }, [dateFilter]);

    useEffect(() => { fetchFlights(); }, [fetchFlights]);
    // Auditoria 5: auto-refresh 30s de la lista de vuelos de rampa
    useAutoRefresh(fetchFlights, 30000);

    const selectFlight = async (flight) => {
        setSelectedFlight(flight);
        try {
            const res = await axios.get(`${API}/ramp/flights/${flight.id}/services`, { headers });
            setServices(res.data || {});
        } catch { setServices({}); }
    };

    // Bloque B2: cada casilla se guarda al momento y registra su actividad del turnaround (hora y usuario)
    const toggleItem = async (key) => {
        if (!selectedFlight) return;
        const prev = services;
        const next = { ...services, [key]: !services[key] };
        setServices(next);
        try {
            await axios.post(`${API}/ramp/flights/${selectedFlight.id}/services`, { [key]: next[key] }, { headers });
            if (next[key]) toast.success('Registrado en el turnaround con hora y usuario');
            const res = await axios.get(`${API}/ramp/flights/${selectedFlight.id}/services`, { headers });
            setServices(res.data || {});
        } catch (e) {
            setServices(prev);
            toast.error(e.response?.data?.detail || 'Error al guardar');
        }
    };

    const saveServices = async () => {
        if (!selectedFlight) return;
        setSaving(true);
        try {
            await axios.post(`${API}/ramp/flights/${selectedFlight.id}/services`, services, { headers });
            toast.success('Servicios de rampa guardados');
            fetchFlights();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
        finally { setSaving(false); }
    };

    const markComplete = async () => {
        if (!selectedFlight) return;
        setSaving(true);
        try {
            await axios.post(`${API}/ramp/flights/${selectedFlight.id}/services`, { ...services, completed: true }, { headers });
            toast.success('Servicios de rampa completados');
            fetchFlights();
            setServices(prev => ({ ...prev, completed: true }));
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        finally { setSaving(false); }
    };

    const checkedCount = checklistItems.filter(i => services[i.key]).length;
    const progress = Math.round((checkedCount / checklistItems.length) * 100);
    const canManage = hasRole('admin') || hasRole('ramp') || hasRole('ccod');
    const canManageFuel = hasRole('admin') || hasRole('ramp') || hasRole('dispatcher') || hasRole('maintenance');
    const baggageInfo = services.baggage_from_counter;
    // Auditoria 6 - Item 12: unidad de combustible configurable por aeronave
    const fuelUnit = selectedFlight?.aircraft_fuel_unit || airline?.fuel_unit || 'kg';

    return (
        <div className="space-y-6" data-testid="ramp-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="ramp-title">Rampa</h1>
                    <p className="text-muted-foreground mt-1">Servicios de plataforma y checklist de turnaround</p>
                </div>
                <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="w-[160px] input-aviation font-mono" data-testid="ramp-date-filter" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
                {/* Flight list */}
                <div className="lg:col-span-4 space-y-3">
                    <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Vuelos del Dia</h2>
                    {loading ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                    ) : flights.length === 0 ? (
                        <Card className="card-aviation"><CardContent className="p-8 text-center text-muted-foreground"><Plane className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No hay vuelos</p></CardContent></Card>
                    ) : flights.map(f => (
                        <Card
                            key={f.id}
                            className={`card-aviation cursor-pointer transition-all ${selectedFlight?.id === f.id ? 'ring-2 ring-primary' : 'hover:border-primary/30'}`}
                            onClick={() => selectFlight(f)}
                            data-testid={`ramp-flight-${f.id}`}
                        >
                            <CardContent className="p-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-bold font-mono text-base">{f.flight_number}</p>
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <span className="font-mono">{f.origin}</span>
                                            <ArrowRight className="w-3 h-3" />
                                            <span className="font-mono">{f.destination}</span>
                                        </div>
                                    </div>
                                    <div className="text-right space-y-1">
                                        <Badge className={statusColors[f.status] || 'bg-muted'}>{statusLabels[f.status] || f.status}</Badge>
                                        {f.ramp_completed ? (
                                            <div className="flex items-center gap-1 justify-end text-emerald-500 text-xs"><CheckCircle className="w-3 h-3" />Completo</div>
                                        ) : f.ramp_services ? (
                                            <div className="text-xs text-amber-500">En progreso</div>
                                        ) : (
                                            <div className="text-xs text-muted-foreground">Pendiente</div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                    <Clock className="w-3 h-3" />
                                    <span className="font-mono">{f.scheduled_departure?.split('T')[1]?.slice(0,5) || '--:--'}</span>
                                    {f.aircraft_registration && <Badge variant="outline" className="ml-auto text-[10px] font-mono">{f.aircraft_registration}</Badge>}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Ramp services panel */}
                <div className="lg:col-span-8">
                    {!selectedFlight ? (
                        <Card className="card-aviation"><CardContent className="p-12 text-center text-muted-foreground"><Package className="w-14 h-14 mx-auto mb-4 opacity-20" /><p className="text-lg">Selecciona un vuelo para gestionar servicios de rampa</p></CardContent></Card>
                    ) : (
                        <div className="space-y-4">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Plane className="w-5 h-5 text-primary" />
                                    <div>
                                        <h2 className="text-xl font-bold font-mono">{selectedFlight.flight_number}</h2>
                                        <p className="text-sm text-muted-foreground">{selectedFlight.origin} → {selectedFlight.destination} | {selectedFlight.aircraft_registration || 'Sin A/C'}</p>
                                    </div>
                                </div>
                                {services.completed && <Badge className="bg-emerald-500/10 text-emerald-500 text-sm">Servicios Completados</Badge>}
                                <Button variant="outline" size="sm" onClick={() => navigate(`/turnaround/flight/${selectedFlight.id}`)} data-testid="ramp-open-turnaround">Ver turnaround</Button>
                            </div>

                            {/* Progress */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">Progreso del checklist</span>
                                    <span className="font-mono font-bold">{checkedCount}/{checklistItems.length} ({progress}%)</span>
                                </div>
                                <Progress value={progress} className="h-2" />
                            </div>

                            {/* Checklist */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="ramp-checklist">
                                {checklistItems.map(item => {
                                    const Icon = item.icon;
                                    const checked = !!services[item.key];
                                    const isFuelItem = item.key === 'fuel_completed';
                                    const canToggle = canManage || (isFuelItem && canManageFuel);
                                    return (
                                        <div
                                            key={item.key}
                                            className={`flex items-center justify-between gap-3 p-3 md:p-4 rounded-lg border transition-colors ${checked ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-muted/20 border-border/30'}`}
                                            data-testid={`ramp-item-${item.key}`}
                                        >
                                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                <Icon className={`w-5 h-5 flex-shrink-0 ${checked ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                                                <div className="min-w-0 flex-1">
                                                    <span className={`text-sm block leading-tight ${checked ? 'font-medium' : 'text-muted-foreground'}`}>{item.label}</span>
                                                    <span className="text-[10px] text-muted-foreground/60 block leading-tight mt-0.5">{item.responsible}</span>
                                                </div>
                                            </div>
                                            {canToggle && !services.completed && (
                                                <Switch
                                                    checked={checked}
                                                    onCheckedChange={() => toggleItem(item.key)}
                                                    data-testid={`switch-${item.key}`}
                                                />
                                            )}
                                            {(services.completed || !canToggle) && (
                                                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${checked ? 'bg-emerald-500/20 text-emerald-500' : 'bg-muted'}`}>
                                                    {checked && <CheckCircle className="w-3 h-3" />}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Counter baggage info */}
                            {baggageInfo && baggageInfo.pax_count > 0 && (
                                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20" data-testid="counter-baggage-info">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-2 flex items-center gap-1.5">
                                        <Luggage className="w-3.5 h-3.5" />Equipaje registrado por Counter
                                    </h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                                        <div><span className="text-muted-foreground text-xs">PAX:</span> <span className="font-mono font-bold">{baggageInfo.pax_count}</span></div>
                                        <div><span className="text-muted-foreground text-xs">Check-in:</span> <span className="font-mono font-bold">{baggageInfo.checked_in_count}</span></div>
                                        <div><span className="text-muted-foreground text-xs">Bultos:</span> <span className="font-mono font-bold">{baggageInfo.total_bags}</span></div>
                                        <div><span className="text-muted-foreground text-xs">Peso:</span> <span className="font-mono font-bold">{baggageInfo.total_weight_kg} kg</span></div>
                                    </div>
                                </div>
                            )}

                            {/* Extra fields */}
                            {canManageFuel && !services.completed && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <div className="flex flex-col gap-0.5">
                                            <Label>Combustible ({fuelUnit})</Label>
                                            <span className="text-[10px] text-muted-foreground/60">Responsable: Despacho / Rampa</span>
                                        </div>
                                        <Input type="number" min="0" value={services.fuel_liters || 0} onChange={e => setServices({...services, fuel_liters: parseInt(e.target.value) || 0})} data-testid="ramp-fuel" />
                                        {services.fuel_updated_by && <span className="text-[10px] text-muted-foreground">Actualizado por: {services.fuel_updated_by}</span>}
                                    </div>
                                    {canManage && (
                                        <div className="space-y-1.5">
                                            <Label>Carga (kg)</Label>
                                            <Input type="number" min="0" value={services.cargo_kg || 0} onChange={e => setServices({...services, cargo_kg: parseInt(e.target.value) || 0})} data-testid="ramp-cargo" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {canManage && !services.completed && (
                                <div className="space-y-1.5">
                                    <Label>Notas</Label>
                                    <Textarea value={services.notes || ''} onChange={e => setServices({...services, notes: e.target.value})} placeholder="Notas adicionales..." rows={2} data-testid="ramp-notes" />
                                </div>
                            )}

                            {/* Actions */}
                            {canManage && !services.completed && (
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                    <Button onClick={saveServices} disabled={saving} className="flex-1 h-11 sm:h-10" variant="outline" data-testid="ramp-save">
                                        {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar Progreso
                                    </Button>
                                    <Button onClick={markComplete} disabled={saving || progress < 80} className="flex-1 h-11 sm:h-10 btn-primary" data-testid="ramp-complete">
                                        <CheckCircle className="w-4 h-4 mr-2" />Marcar Completo
                                    </Button>
                                </div>
                            )}
                            {!canManage && canManageFuel && !services.completed && (
                                <Button onClick={saveServices} disabled={saving} variant="outline" className="h-11 sm:h-10" data-testid="ramp-save-fuel">
                                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar Combustible
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
